import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Trash2, ChevronDown, Loader2, Gift, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useTerminal } from "@/lib/terminal";
import { errMsg } from "@/lib/api";
import { EXPLORER, short, usd } from "@/lib/format";

const Portfolio = ({ w }) => {
  const { api, sell, busy, settings } = useTerminal();
  const [pf, setPf] = useState(null);
  const [claiming, setClaiming] = useState("");
  const load = useCallback(async () => {
    try { setPf(await api("get", `/wallets/${w.id}/portfolio`)); } catch (e) { toast.error(errMsg(e)); setPf({ tokens: [] }); }
  }, [api, w.id]);
  useEffect(() => { load(); }, [load]);

  const claim = async (token, kind) => {
    setClaiming(`${token}-${kind}`);
    try { await api("post", `/wallets/${w.id}/claim`, { token, kind }); toast.success("Claim başarılı"); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setClaiming(""); }
  };
  const doSell = async (token, p, symbol) => { if (await sell(token, p, symbol, w.id)) load(); };

  if (!pf) return <div className="p-4 flex items-center gap-2 text-xs text-slate-500"><Loader2 className="w-4 h-4 animate-spin" />Tokenler taranıyor…</div>;
  return (
    <div className="border-t border-[#222634]" data-testid={`wallet-portfolio-${w.id}`}>
      <div className="flex items-center gap-4 px-4 py-2 text-xs font-mono text-slate-400">
        <span>Token değeri <span className="text-slate-100">{usd(pf.total_usd)}</span></span>
        <span>Claim edilebilir <span className="text-[#FFD700]">{Number(pf.claimable_eth || 0).toFixed(6)} Ξ</span></span>
        <button data-testid={`wallet-refresh-${w.id}`} onClick={load} className="ml-auto hover:text-slate-100"><RefreshCw className="w-3.5 h-3.5" /></button>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <tbody>
            {pf.tokens.map((t) => (
              <tr key={t.token} className="border-t border-[#222634]/50 hover:bg-[#161920]" data-testid={`portfolio-row-${w.id}-${t.token}`}>
                <td className="px-4 py-2"><Link to={`/token/${t.token}`} className="flex items-center gap-2 hover:text-[#00E676]">{t.image && <img src={t.image} alt="" className="w-6 h-6 rounded" />}<span className="font-sans font-semibold">{t.symbol}</span></Link></td>
                <td className="px-2 text-right text-slate-300">{t.balance.toLocaleString("tr-TR", { maximumFractionDigits: 0 })}</td>
                <td className="px-2 text-right">{usd(t.value_usd)}</td>
                <td className="px-2">
                  <div className="flex gap-1 justify-end">
                    {t.dividend_eth > 0 && <button data-testid={`claim-dividend-${w.id}-${t.token}`} disabled={!!claiming} onClick={() => claim(t.token, "dividend")} className="flex items-center gap-1 px-2 py-1 rounded border border-[#FFD700]/40 text-[#FFD700] hover:bg-[#FFD700]/10 disabled:opacity-40">{claiming === `${t.token}-dividend` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gift className="w-3 h-3" />}Ödül {t.dividend_eth.toFixed(5)}</button>}
                    {t.creator_eth > 0 && <button data-testid={`claim-creator-${w.id}-${t.token}`} disabled={!!claiming} onClick={() => claim(t.token, "creator")} className="flex items-center gap-1 px-2 py-1 rounded border border-[#00F0FF]/40 text-[#00F0FF] hover:bg-[#00F0FF]/10 disabled:opacity-40">{claiming === `${t.token}-creator` ? <Loader2 className="w-3 h-3 animate-spin" /> : <Gift className="w-3 h-3" />}Creator fee {t.creator_eth.toFixed(5)}</button>}
                  </div>
                </td>
                <td className="px-4"><div className="flex justify-end gap-1">
                  {t.balance > 0 && settings.sell_presets.map((p) => (
                    <button key={p} disabled={busy[`sell-${t.token}-${w.id}`]} data-testid={`portfolio-sell-${p}-${w.id}-${t.token}`} onClick={() => doSell(t.token, p, t.symbol)}
                      className="px-2 py-1 rounded border border-[#FF5252]/30 text-[#FF5252] hover:bg-[#FF5252]/15 transition-colors disabled:opacity-40">%{p}</button>
                  ))}
                </div></td>
              </tr>
            ))}
          </tbody>
        </table>
        {pf.tokens.length === 0 && <p className="px-4 py-3 text-xs text-slate-500">Bu cüzdanda full.fun tokeni yok</p>}
      </div>
    </div>
  );
};

export const WalletCard = ({ w, onChange }) => {
  const { api } = useTerminal();
  const [open, setOpen] = useState(false);
  const patch = async (data) => {
    try { await api("patch", `/wallets/${w.id}`, data); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!window.confirm(`${w.label} silinsin mi?`)) return;
    try { await api("delete", `/wallets/${w.id}`); toast("Cüzdan silindi"); onChange(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-md border border-[#222634] bg-[#101216] overflow-hidden" data-testid={`wallet-card-${w.id}`}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 cursor-pointer hover:bg-[#13161B]" onClick={() => setOpen(!open)} data-testid={`wallet-expand-${w.id}`}>
        <span className="flex items-center gap-2 min-w-0">
          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
          <input data-testid={`wallet-label-${w.id}`} defaultValue={w.label} onClick={(e) => e.stopPropagation()} onBlur={(e) => e.target.value !== w.label && patch({ label: e.target.value })}
            className="bg-transparent font-semibold outline-none w-28 focus:border-b focus:border-[#333A4E]" />
        </span>
        <span className="flex items-center gap-1.5 font-mono text-xs text-slate-400" onClick={(e) => e.stopPropagation()}>
          <a href={`${EXPLORER}/address/${w.address}`} target="_blank" rel="noreferrer" className="hover:text-slate-100">{short(w.address)}</a>
          <button data-testid={`wallet-copy-${w.id}`} onClick={() => { navigator.clipboard.writeText(w.address); toast.success("Adres kopyalandı"); }}><Copy className="w-3 h-3 hover:text-slate-100" /></button>
        </span>
        <span className="font-mono text-sm text-slate-100" data-testid={`wallet-balance-${w.id}`}>{Number(w.eth_balance).toFixed(5)} Ξ</span>
        <div className="ml-auto flex items-center gap-4 text-xs text-slate-400" onClick={(e) => e.stopPropagation()}>
          <label className="flex items-center gap-1.5">Trade<Switch data-testid={`wallet-trade-switch-${w.id}`} checked={w.trade} onCheckedChange={(v) => patch({ trade: v })} /></label>
          <label className="flex items-center gap-1.5">Bot<Switch data-testid={`wallet-bot-switch-${w.id}`} checked={w.bot} onCheckedChange={(v) => patch({ bot: v })} /></label>
          <button data-testid={`wallet-delete-${w.id}`} onClick={remove} className="text-slate-500 hover:text-[#FF5252]"><Trash2 className="w-4 h-4" /></button>
        </div>
      </div>
      {open && <Portfolio w={w} />}
    </div>
  );
};
